import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'input[type=number], input[numbersOnly]',
  standalone: false,
})
export class OnlyNumbersDirective implements OnInit, OnDestroy, OnChanges {
  @Input() decimal: boolean = false; // Specifies if decimal numbers are allowed - in that case, different regex is used.
  @Input() decimalSeparator: string = '.';
  @Input() min: number = -Infinity;
  @Input() max: number = Infinity;
  @Input() pattern?: string | RegExp;

  private regex: RegExp | null = null;
  private sanitizeRegex!: RegExp;
  private subs: Subscription = new Subscription();

  private readonly el: ElementRef<HTMLInputElement> = inject<ElementRef<HTMLInputElement>>(ElementRef);

  ngOnInit(): void {
    const input: HTMLInputElement = this.el.nativeElement;

    this.subs.add(fromEvent<KeyboardEvent>(input, 'keydown').subscribe(e => this.onKeyDown(e)));
    this.subs.add(fromEvent<ClipboardEvent>(input, 'paste').subscribe(e => this.onPasteOrDrop(e)));
    this.subs.add(fromEvent<DragEvent>(input, 'drop').subscribe(e => this.onPasteOrDrop(e)));

    this.sanitizeRegex = new RegExp(`[^0-9${this.decimalSeparator}]`, 'g');
  }

  /**
   * Converts input min and max values to numbers. If there is no valid input (like abc) then change it to -∞ or ∞.
   * @param changes - Changes to input bindings
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pattern) {
      this.regex = this.pattern ? new RegExp(this.pattern) : null;
    }

    this.min = isNaN(Number(this.min)) ? -Infinity : Number(this.min);
    this.max = isNaN(Number(this.max)) ? Infinity : Number(this.max);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.subs = undefined!;
  }

  /**
   * Handles keydown event, restricting input based on configuration.
   * @param event - KeyboardEvent to evaluate
   */
  private onKeyDown(event: KeyboardEvent): void {
    if (this.isAllowedKey(event)) {
      return;
    }

    const input: HTMLInputElement = this.el.nativeElement;
    const key: string = event.key;
    /**
     * Predict the next input value based on the current input and the pressed key (getNextInputValue method)
     * so it can be validated following way:
     * - If newValue would contain more than one decimal separator ('.' or ',') then prevent insertion.
     */
    const newValue: string = this.getNextInputValue(key);

    // If input is decimal and the key is the decimal separator, check if it should be blocked.
    if (this.decimal && key === this.decimalSeparator) {
      if (this.shouldBlockDecimalSeparator(input, newValue)) {
        event.preventDefault();
      }
      return;
    }

    // Prevent insertion of non-numeric characters.
    if (key === ' ' || isNaN(Number(key))) {
      event.preventDefault();
      return;
    }

    // If a regex pattern is provided via Input, validate against it.
    if (this.regex && !this.regex.test(newValue)) {
      event.preventDefault();
      return;
    }

    // If the new value is outside the min/max range, prevent insertion.
    const num: number = Number(newValue);
    if (num < this.min || num > this.max) {
      event.preventDefault();
    }
  }

  /**
   * Checks if user is not entering more than one decimal separator.
   * @param {string} key
   * @param {HTMLInputElement} input
   * @param {string} newValue
   * @returns {boolean}
   * @private
   */
  private shouldBlockDecimalSeparator(input: HTMLInputElement, newValue: string): boolean {
    const escapedSeparator: string = this.decimalSeparator === '.' ? '\\.' : this.decimalSeparator;
    const separatorCount: number = (newValue.match(new RegExp(`${escapedSeparator}`, 'g')) || []).length;
    return separatorCount > 1;
  }

  /**
   * Handles both paste and drop events, sanitizing input before inserting via insertSanitizedText method.
   * @param event - Clipboard or DragEvent containing input data
   */
  private onPasteOrDrop(event: ClipboardEvent | DragEvent): void {
    const text: string = 'clipboardData' in event
              ? event.clipboardData?.getData('text/plain') || ''
              : event.dataTransfer?.getData('text') || '';

    if (!text) {
      return;
    }

    event.preventDefault();
    this.insertSanitizedText(this.sanitizeInput(text));
  }

  /**
   * Checks if a key is allowed (navigation or copy/paste keys).
   * @param e - KeyboardEvent
   * @returns Whether the key is allowed
   */
  private isAllowedKey(e: KeyboardEvent): boolean {
    const allowedKeys: Set<string> = new Set([
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'Home', 'End', 'ArrowLeft', 'ArrowRight',
    ]);

    const ctrlCmd: boolean = e.ctrlKey || e.metaKey;
    return allowedKeys.has(e.key) || (ctrlCmd && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()));
  }

  /**
   * Simulate what the input field's value would be if a given key or character were inserted at the current cursor position.
   * Serves for validation or previewing the result of a key press before actually updating the input field.
   * @param key - The pressed key or character
   * @returns Simulated next input value
   */
  private getNextInputValue(key: string): string {
    const input: HTMLInputElement = this.el.nativeElement;
    const { selectionStart, selectionEnd, value } = input;

    return value.slice(0, selectionStart!) + key + value.slice(selectionEnd!);
  }

  /**
   * Sanitizes pasted or dropped input according to directive config.
   * @param input - Raw input string
   * @returns Cleaned input string
   */
  private sanitizeInput(input: string): string {
    // If decimal is true, replace all non-numeric characters except the decimal separator. If false, remove all non-numeric characters.
    const clean: string = this.decimal
      ? input.replace(this.sanitizeRegex, '')
      : input.replace(/\D/g, '');

    const maxLength: number = this.el.nativeElement.maxLength;

    // If maxLength is set, limit the length of the sanitized input.
    const allowedLength: number = maxLength > 0
      ? maxLength - this.el.nativeElement.value.length
      : clean.length;

    // Remove all characters beyond the allowed length.
    return clean.slice(0, allowedLength);
  }

  /**
   * Inserts sanitized text into the input at the cursor position.
   * @param text - Text to insert
   */
  private insertSanitizedText(text: string): void {
    const input: HTMLInputElement = this.el.nativeElement;
    const start: number = input.selectionStart!;
    const end: number = input.selectionEnd!;

    input.value = input.value.slice(0, start) + text + input.value.slice(end);
    input.setSelectionRange(start + text.length, start + text.length);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
